import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  CheckCircle,
  MapPin,
  Shield,
  Info,
  ChevronsUpDown,
  Check,
  Pill,
  Syringe,
  ArrowRight,
  Package,
  ExternalLink,
} from "lucide-react";
import { CircularProgress } from "@mui/material";
import {
  DosageCalculatorProps,
  CalculatorResult,
} from "../dosageCalculators/types";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { vaccineDatabase, getVaccineById } from "./vaccineDatabase";
import useTitanApp from "../../../../../hooks/useTitanApp";
import { getTravelOutbreaks } from "@/dispatchers/patientDispatchers";
import {
  type TravelDestination,
  getTravelHealthProUrl,
} from "./travelDestinations";

interface MalariaOption {
  prescribeItemId: string;
  name: string;
  medication: string;
  medicationType: string;
  dosage: string;
  strength: string;
  quantityFormula: (days: number) => string;
  duration: (days: number) => string;
  notes: string;
  suitableFor: string[];
  notSuitableFor: string[];
}

interface MalariaCalculation {
  dosage: string;
  quantity: string;
  duration: string;
  notes: string;
}

const calculateMalariaDosing = (
  optionId: string,
  days: number,
  isChild: boolean,
  weight?: number,
): MalariaCalculation => {
  switch (optionId) {
    case "59a2ac8b-bfa2-4d57-86c8-737565944379": {
      // Adults and children over 12: 100mg daily
      const total = 2 + days + 28;
      return {
        dosage: "Take 1 capsule daily with food or milk",
        quantity: `${total} capsules (100mg)`,
        duration: `${total} days (start 2 days before travel)`,
        notes:
          "Start 2 days before entering an endemic area and be continued until 4 weeks after leaving the area. Take with plenty of water or milk.",
      };
    }
    case "ce251889-3b48-487a-bbf3-c48fd479fe0f": {
      // Weight-based dosing for children
      if (isChild && weight) {
        const total = 2 + days + 7;
        if (weight >= 40) {
          // Adult dose
          return {
            dosage: "Take 1 adult tablet daily with food or milk",
            quantity: `${total} adult tablets (250mg/100mg)`,
            duration: `${total} days (start 1-2 days before travel)`,
            notes:
              "Start 1-2 days before travel, continue during travel, and for 7 days after return. Take at the same time each day with food or milk. Generally well tolerated.",
          };
        } else if (weight >= 31) {
          // 31-40kg: 3 pediatric tablets
          return {
            dosage: "Take 3 pediatric tablets daily with food or milk",
            quantity: `${total * 3} pediatric tablets (62.5mg/25mg each)`,
            duration: `${total} days (start 1-2 days before travel)`,
            notes:
              "Start 1-2 days before travel, continue during travel, and for 7 days after return. Take at the same time each day with food or milk. Weight: 31-40kg.",
          };
        } else if (weight >= 21) {
          // 21-30kg: 2 pediatric tablets
          return {
            dosage: "Take 2 pediatric tablets daily with food or milk",
            quantity: `${total * 2} pediatric tablets (62.5mg/25mg each)`,
            duration: `${total} days (start 1-2 days before travel)`,
            notes:
              "Start 1-2 days before travel, continue during travel, and for 7 days after return. Take at the same time each day with food or milk. Weight: 21-30kg.",
          };
        } else if (weight >= 11) {
          // 11-20kg: 1 pediatric tablet
          return {
            dosage: "Take 1 pediatric tablet daily with food or milk",
            quantity: `${total} pediatric tablets (62.5mg/25mg)`,
            duration: `${total} days (start 1-2 days before travel)`,
            notes:
              "Start 1-2 days before travel, continue during travel, and for 7 days after return. Take at the same time each day with food or milk. Weight: 11-20kg.",
          };
        }
      }
      // Adult dose (default)
      const total = 2 + days + 7;
      return {
        dosage: "Take 1 tablet daily with food or milk",
        quantity: `${total} tablets (250mg/100mg)`,
        duration: `${total} days (start 1-2 days before travel)`,
        notes:
          "Start 1-2 days before travel, continue during travel, and for 7 days after return. Take at the same time each day with food or milk. Generally well tolerated.",
      };
    }
    case "70e08799-ff56-4f96-abeb-87f559ad2f77": {
      // Weight-based dosing for children
      const weeksOfTravel = Math.ceil(days / 7);
      if (isChild && weight) {
        if (weight >= 45) {
          // Adult dose: 250mg weekly
          const total = 3 + weeksOfTravel + 4;
          const totalWeeks = total;
          return {
            dosage: "Take 1 tablet (250mg) weekly with food",
            quantity: `${total} tablets (250mg)`,
            duration: `${totalWeeks} weeks (start 2-3 weeks before travel)`,
            notes:
              "Start 2-3 weeks before travel, take weekly during travel, and for 4 weeks after return. Take on the same day each week with food. Weight: ≥45kg.",
          };
        } else if (weight >= 31) {
          // 31-44kg: 3/4 tablet (187.5mg)
          const total = 3 + weeksOfTravel + 4;
          return {
            dosage: "Take 3/4 tablet (187.5mg) weekly with food",
            quantity: `${Math.ceil(total * 0.75)} tablets (250mg) - divide as needed`,
            duration: `${total} weeks (start 2-3 weeks before travel)`,
            notes:
              "Start 2-3 weeks before travel, take weekly during travel, and for 4 weeks after return. Take 3/4 of a 250mg tablet (187.5mg) on the same day each week with food. Weight: 31-44kg.",
          };
        } else if (weight >= 20) {
          // 20-30kg: 1/2 tablet (125mg)
          const total = 3 + weeksOfTravel + 4;
          return {
            dosage: "Take 1/2 tablet (125mg) weekly with food",
            quantity: `${Math.ceil(total * 0.5)} tablets (250mg) - divide in half`,
            duration: `${total} weeks (start 2-3 weeks before travel)`,
            notes:
              "Start 2-3 weeks before travel, take weekly during travel, and for 4 weeks after return. Take 1/2 of a 250mg tablet (125mg) on the same day each week with food. Weight: 20-30kg.",
          };
        } else if (weight >= 5) {
          // 5-19kg: 1/4 tablet (62.5mg)
          const total = 3 + weeksOfTravel + 4;
          return {
            dosage: "Take 1/4 tablet (62.5mg) weekly with food",
            quantity: `${Math.ceil(total * 0.25)} tablets (250mg) - divide into quarters`,
            duration: `${total} weeks (start 2-3 weeks before travel)`,
            notes:
              "Start 2-3 weeks before travel, take weekly during travel, and for 4 weeks after return. Take 1/4 of a 250mg tablet (62.5mg) on the same day each week with food. Weight: 5-19kg.",
          };
        }
      }
      // Adult dose (default)
      const total = 3 + weeksOfTravel + 4;
      const totalWeeks = 3 + weeksOfTravel + 4;
      return {
        dosage: "Take 1 tablet weekly with food",
        quantity: `${total} tablets (250mg)`,
        duration: `${totalWeeks} weeks (start 2-3 weeks before travel)`,
        notes:
          "Start 2-3 weeks before travel, take weekly during travel, and for 4 weeks after return. Take on the same day each week with food. Not suitable for those with psychiatric history.",
      };
    }
    case "2ff5b199-11e8-4be6-8e05-dce1e86aef40": {
      const weeksOfTravel = Math.ceil(days / 7);
      const chloroquineTotal = 1 + weeksOfTravel + 4;
      return {
        dosage: "2 tablets weekly",
        quantity: `${chloroquineTotal * 2} tablets (250mg)`,
        duration: `${1 + Math.ceil(days / 7) + 4} weeks`,
        notes:
          "Start 1 week before, weekly during, 4 weeks after. May have a bitter taste and can be mixed with food if needed.",
      };
    }
    default:
      return {
        dosage: "",
        quantity: "",
        duration: "",
        notes: "",
      };
  }
};

const malariaOptions: MalariaOption[] = [
  {
    prescribeItemId: "59a2ac8b-bfa2-4d57-86c8-737565944379",
    name: "Doxycycline",
    strength: "100mg",
    medicationType: "capsules",
    medication: "Doxycycline 100mg capsules",
    dosage: "Take 1 capsule daily with food or milk",
    quantityFormula: (days) => {
      const total = 2 + days + 28;
      return `${total} capsules`;
    },
    duration: (days) => {
      const total = 2 + days + 28;
      return `${total} days (start 2 days before travel)`;
    },
    notes:
      "Start 2 days before entering an endemic area and be continued until 4 weeks after leaving the area. Take with plenty of water or milk.",
    suitableFor: ["adults", "children over 12"],
    notSuitableFor: ["pregnancy", "children under 12", "severe liver disease"],
  },
  {
    prescribeItemId: "ce251889-3b48-487a-bbf3-c48fd479fe0f",
    name: "Atovaquone/Proguanil",
    strength: "250mg/100mg",
    medicationType: "tablets",
    medication: "Atovaquone/Proguanil 250mg/100mg",
    dosage: "Take 1 tablet daily with food or milk",
    quantityFormula: (days) => {
      const total = 2 + days + 7;
      return `${total} tablets`;
    },
    duration: (days) => {
      const total = 2 + days + 7;
      return `${total} days (start 1-2 days before travel)`;
    },
    notes:
      "Start 1-2 days before travel, continue during travel, and for 7 days after return. Take at the same time each day with food or milk. Generally well tolerated.",
    suitableFor: ["adults", "children over 11kg", "pregnancy (if needed)"],
    notSuitableFor: ["severe renal impairment", "weight under 11kg"],
  },
  {
    prescribeItemId: "70e08799-ff56-4f96-abeb-87f559ad2f77",
    name: "Mefloquine",
    strength: "250mg",
    medicationType: "tablets",
    medication: "Mefloquine 250mg",
    dosage: "Take 1 tablet weekly with food",
    quantityFormula: (days) => {
      const weeksOfTravel = Math.ceil(days / 7);
      const total = 3 + weeksOfTravel + 4;
      return `${total} tablets`;
    },
    duration: (days) => {
      const weeksOfTravel = Math.ceil(days / 7);
      const totalWeeks = 3 + weeksOfTravel + 4;
      return `${totalWeeks} weeks (start 2-3 weeks before travel)`;
    },
    notes:
      "Start 2-3 weeks before travel, take weekly during travel, and for 4 weeks after return. Take on the same day each week with food. Not suitable for those with psychiatric history.",
    suitableFor: ["adults", "children over 5kg"],
    notSuitableFor: [
      "psychiatric disorders",
      "seizure disorders",
      "cardiac conduction problems",
      "severe liver disease",
    ],
  },
  {
    prescribeItemId: "2ff5b199-11e8-4be6-8e05-dce1e86aef40",
    name: "Chloroquine phosphate",
    strength: "250mg",
    medicationType: "tablets",
    medication: "Chloroquine phosphate 250mg tablets",
    dosage: "2 tablets weekly",
    quantityFormula: (days) => {
      const weeksOfTravel = Math.ceil(days / 7);
      const chloroquineTotal = 1 + weeksOfTravel + 4;
      return `${chloroquineTotal * 2} tablets`;
    },
    duration: (days) => {
      return `${1 + Math.ceil(days / 7) + 4} weeks`;
    },
    notes:
      "Start 1 week before, weekly during, 4 weeks after. May have a bitter taste and can be mixed with food if needed.",
    suitableFor: ["adults", "areas with low chloroquine resistance"],
    notSuitableFor: ["epilepsy", "psoriasis", "severe renal impairment"],
  },
];

type Step =
  | "destination"
  | "selection"
  | "vaccinations"
  | "malaria-duration"
  | "malaria-medication"
  | "review";

export const TravelHealthCalculator: React.FC<DosageCalculatorProps> = ({
  onComplete,
  onCancel,
  setIsLoading,
  isLoading,
  patientInfo,
  isDarkMode = false,
}) => {
  const { patientState } = useTitanApp();
  const { travelDestinations } = patientState || {};
  const [step, setStep] = useState<Step>("destination");
  const [selectedDestination, setSelectedDestination] = useState<string>("");
  // const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  // User's choice of what they need
  const [needVaccinations, setNeedVaccinations] = useState(false);
  const [needMalaria, setNeedMalaria] = useState(false);
  // Vaccination state
  const [selectedVaccines, setSelectedVaccines] = useState<string[]>([]);
  const [vaccineBrands, setVaccineBrands] = useState<Record<string, string>>(
    {},
  );

  // Malaria calculation state
  const [travelDays, setTravelDays] = useState<string>("");
  const [riskLevel, setRiskLevel] = useState<string>("");
  const [selectedMalariaOption, setSelectedMalariaOption] =
    useState<string>("");
  const [childWeight, setChildWeight] = useState<string>(
    patientInfo?.weight?.toString() || "",
  );

  // Outbreak warning state
  const [outbreaks, setOutbreaks] = useState<any[]>([]);
  const [outbreakConfirmed, setOutbreakConfirmed] = useState(false);
  const [isCheckingOutbreaks, setIsCheckingOutbreaks] = useState(false);
  const rawDestination = travelDestinations?.find(
    (d) => (d.id || d._id) === selectedDestination,
  );
  // Map API vaccine data to component format without mutating original object
  const destination = rawDestination
    ? {
        ...rawDestination,
        vaccines:
          rawDestination.travelVaccine ||
          (rawDestination as any).travelVaccine?.map((v: any) => ({
            ...v,
            id: v.id || v._id,
            type: v.type === "routine" ? "recommended" : v.type,
          })) ||
          [],
      }
    : null;
  const hasMalariaRisk = destination && destination.malariaRisk;

  const formatOutbreakDate = (value?: string) => {
    if (!value) return "Date unavailable";
    const parsed = Date.parse(value);
    if (Number.isNaN(parsed)) return "Date unavailable";
    return new Date(parsed).toLocaleDateString();
  };

  const extractMonthYearFromDescription = (description?: string) => {
    if (!description) return "";
    const monthNames = [
      "january",
      "february",
      "march",
      "april",
      "may",
      "june",
      "july",
      "august",
      "september",
      "october",
      "november",
      "december",
    ];

    const monthRegex = new RegExp(
      `\\b(${monthNames.join("|")})\\b\\s+(\\d{4})`,
      "gi",
    );

    const matches = [...description.matchAll(monthRegex)].map((match) => ({
      month: match[1],
      year: match[2],
    }));

    if (matches.length === 0) return "";

    const format = (m) =>
      `${m.month[0].toUpperCase()}${m.month.slice(1).toLowerCase()} ${m.year}`;

    if (matches.length === 1) return format(matches[0]);

    return `${format(matches[0])} - ${format(matches[matches.length - 1])}`;
  };

  const getOutbreakDateLabel = (outbreak: any) => {
    if (outbreak?.pubDate) {
      const formatted = formatOutbreakDate(outbreak.pubDate);
      return formatted === "Date unavailable" ? "" : formatted;
    }
    return extractMonthYearFromDescription(outbreak?.description);
  };
  const calculateAge = (dob) => {
    if (!dob) return null;

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };
  // Determine if patient is a child (under 18)
  const age = calculateAge(patientInfo?.dob);
  const isChild = age !== null && age < 18;
  // const isChild = patientInfo?.dob ? patientInfo.dob < 18 : false;
  const needsWeightInput = isChild && !childWeight;

  const getGenderLabel = (sex) => {
    const genderMap = {
      0: "Not Known",
      1: "Male",
      2: "Female",
      3: "Other",
      9: "Not Specified",
    };

    return genderMap[sex] || "Unknown";
  };

  const gender = getGenderLabel(patientInfo?.sex);

  // Auto-set risk level based on destination's risk level
  React.useEffect(() => {
    if (destination && !riskLevel) {
      // Map destination riskLevel to user-friendly risk level
      const mappedRiskLevel =
        destination.riskLevel === "high"
          ? "high"
          : destination.riskLevel === "medium"
            ? "moderate"
            : "low";
      setRiskLevel(mappedRiskLevel);
    }
  }, [destination?._id, riskLevel]);

  // Check for outbreaks when destination is selected
  React.useEffect(() => {
    const checkOutbreaks = async () => {
      if (!destination) {
        setOutbreaks([]);
        setOutbreakConfirmed(false);
        return;
      }

      setIsCheckingOutbreaks(true);
      try {
        const data = await getTravelOutbreaks();
        const outbreaksPayload = Array.isArray(data)
          ? data
          : (data?.response ??
            data?.outbreaks ??
            data?.data?.response ??
            data?.data?.outbreaks ??
            []);

        // Match outbreaks for this country
        const destName = destination.name.toLowerCase();
        const destTokens = destName
          .split(/\s+|,|&|and/)
          .map((t) => t.trim())
          .filter(Boolean);

        const countryOutbreaks = outbreaksPayload.filter((outbreak: any) => {
          const outbreakCountry = (outbreak.country || "").toLowerCase();
          const title = (outbreak.title || "").toLowerCase();
          const description = (outbreak.description || "").toLowerCase();

          if (
            outbreakCountry &&
            (outbreakCountry.includes(destName) ||
              destName.includes(outbreakCountry))
          ) {
            return true;
          }

          const textToCheck = `${title} ${description}`;
          if (textToCheck.includes(destName)) return true;

          // Fallback: match all destination tokens (e.g., "Trinidad" + "Tobago")
          return (
            destTokens.length > 0 &&
            destTokens.every((token) => textToCheck.includes(token))
          );
        });
        setOutbreaks(countryOutbreaks);

        // Outbreaks will be shown inline in the UI
      } catch (error) {
        console.error("Error checking outbreaks:", error);
        setOutbreaks([]);
      } finally {
        setIsCheckingOutbreaks(false);
      }
    };

    checkOutbreaks();
  }, [destination?._id]);

  // Helper function to get available brands for a vaccine/disease
  const getBrandsForVaccine = (vaccineName: string) => {
    // Search in the vaccine database for this disease
    return vaccineDatabase.filter(
      (vac) => vac.disease.toLowerCase() === vaccineName.toLowerCase(),
    );
  };

  const handleVaccineToggle = (vaccineName: string, checked: boolean) => {
    if (checked) {
      setSelectedVaccines((prev) => [...prev, vaccineName]);
      // Auto-select first brand if available
      const brands = getBrandsForVaccine(vaccineName);
      if (brands.length > 0) {
        setVaccineBrands((prev) => ({
          ...prev,
          [vaccineName]: brands[0].id,
        }));
      }
    } else {
      setSelectedVaccines((prev) => prev.filter((v) => v !== vaccineName));
      // Clear brand selection
      setVaccineBrands((prev) => {
        const newBrands = { ...prev };
        delete newBrands[vaccineName];
        return newBrands;
      });
    }
  };

  const handleBrandChange = (vaccineName: string, brandId: string) => {
    setVaccineBrands((prev) => ({
      ...prev,
      [vaccineName]: brandId,
    }));
  };

  const handleCreateVaccines = () => {
    const results: CalculatorResult[] = [];

    // Add vaccine results only
    if (destination && selectedVaccines.length > 0) {
      selectedVaccines.forEach((vaccineName) => {
        const vaccine = destination.vaccines.find(
          (v) => v.name === vaccineName,
        );

        // Get selected brand information from vaccine database
        const brandId = vaccineBrands[vaccineName];
        const brandInfo = brandId ? getVaccineById(brandId) : null;

        let notes = `${vaccine?.description || ""}\n\n${vaccine?.notes || ""}`;

        // Add brand-specific information if available
        if (brandInfo) {
          notes += `\n\n💊 Brand: ${brandInfo.brandName} (${brandInfo.manufacturer})`;
          if (brandInfo.dosage) {
            notes += `\nDosage: ${brandInfo.dosage}`;
          }
          if (brandInfo.route) {
            notes += `\nRoute: ${brandInfo.route}`;
          }
          if (brandInfo.ageRange) {
            notes += `\nAge Range: ${brandInfo.ageRange}`;
          }
        }

        notes += `\n\nDestination: ${destination.name} (${destination.region})`;

        if (destination.yellowFeverRequired) {
          notes += `\n\n⚠️ IMPORTANT: Yellow Fever certificate is REQUIRED for entry to ${destination.name}.`;
        }

        if (destination.malariaRisk) {
          notes += `\n\n🦟 Malaria Risk: ${destination.malariaRisk}`;
        }

        if (destination.generalAdvice) {
          notes += `\n\nGeneral Advice: ${destination.generalAdvice}`;
        }

        // Add pediatric-specific notes
        if (age !== undefined && age < 18) {
          notes += `\n\n👶 PEDIATRIC PATIENT (Age: ${age} years)`;

          const vaccineLower = vaccineName.toLowerCase();

          if (vaccineLower.includes("yellow fever")) {
            if (age < 2) {
              notes += `\n⚠️ Yellow Fever vaccine should be used with caution in children under 2 years. Specialist advice recommended.`;
            }
          }

          if (vaccineLower.includes("typhoid")) {
            if (age < 6) {
              notes += `\n⚠️ Oral typhoid vaccine not suitable - use injectable form only for children under 6 years.`;
            }
          }

          if (vaccineLower.includes("hepatitis a")) {
            if (age < 2) {
              notes += `\n⚠️ Pediatric dose: 0.5ml (720 ELISA units) for children 1-15 years.`;
            }
          }

          if (vaccineLower.includes("rabies")) {
            notes += `\n⚠️ Pediatric dose: Same volume as adults but may require different schedule. Check patient's weight for dosing.`;
          }

          if (vaccineLower.includes("japanese encephalitis")) {
            if (age < 3) {
              notes += `\n⚠️ Pediatric dose: 0.25ml for children under 3 years (half adult dose).`;
            }
          }

          notes += `\n⚠️ Verify pediatric dosing schedule and formulation before administration.`;
        }

        notes += `\n\n📚 Source: TravelHealthPro (https://travelhealthpro.org.uk/)`;

        // Build medication name with brand if selected
        let medicationName = vaccineName;
        if (brandInfo) {
          medicationName = `${vaccineName} - ${brandInfo.brandName}`;
        }

        results.push({
          medication: medicationName,
          dosage: brandInfo?.schedule || vaccine?.schedule || "As per schedule",
          quantity: "1 course",
          duration: brandInfo?.schedule || vaccine?.schedule || "",
          notes,
          type: "vaccine",
          prescribedItemId: brandInfo?.id || vaccine?.id,
        });
      });
    }

    if (results.length > 0) {
      setIsLoading(true);
      onComplete(results);
    }
  };

  const handleCreateMalaria = () => {
    const results: CalculatorResult[] = [];

    // Add malaria prescription only
    if (selectedMalariaOption && travelDays) {
      const option = malariaOptions.find(
        (opt) => opt.prescribeItemId === selectedMalariaOption,
      );
      if (option) {
        const days = parseInt(travelDays);
        const weight = isChild ? parseFloat(childWeight) : undefined;
        const calculation = calculateMalariaDosing(
          selectedMalariaOption,
          days,
          isChild,
          weight,
        );

        let notes = calculation.notes;
        if (destination) {
          notes += `\n\nMalaria Risk in ${destination.name}: ${destination.malariaRisk}`;
        }
        if (isChild && weight) {
          notes += `\n\n⚠️ PEDIATRIC DOSE: Weight-based dosing for ${weight}kg child. Verify dose appropriateness before dispensing.`;
        }

        results.push({
          medication: option.medication,
          dosage: calculation.dosage,
          quantity: calculation.quantity,
          duration: calculation.duration,
          notes,
          type: "prescription",
        });
      }
    }

    if (results.length > 0) {
      setIsLoading(true);
      onComplete(results);
    }
  };

  const handleCreateBoth = () => {
    const results: CalculatorResult[] = [];
    // Add vaccine results
    if (destination && selectedVaccines.length > 0) {
      selectedVaccines.forEach((vaccineName) => {
        const vaccine = destination.vaccines.find(
          (v) => v.name === vaccineName,
        );

        // Get selected brand information from vaccine database
        const brandId = vaccineBrands[vaccineName];
        const brandInfo = brandId ? getVaccineById(brandId) : null;

        let notes = `${vaccine?.description || ""}\n\n${vaccine?.notes || ""}`;

        // Add brand-specific information if available
        if (brandInfo) {
          notes += `\n\n💊 Brand: ${brandInfo.brandName} (${brandInfo.manufacturer})`;
          if (brandInfo.dosage) {
            notes += `\nDosage: ${brandInfo.dosage}`;
          }
          if (brandInfo.route) {
            notes += `\nRoute: ${brandInfo.route}`;
          }
          if (brandInfo.ageRange) {
            notes += `\nAge Range: ${brandInfo.ageRange}`;
          }
        }

        notes += `\n\nDestination: ${destination.name} (${destination.region})`;

        if (destination.yellowFeverRequired) {
          notes += `\n\n⚠️ IMPORTANT: Yellow Fever certificate is REQUIRED for entry to ${destination.name}.`;
        }

        if (destination.malariaRisk) {
          notes += `\n\n🦟 Malaria Risk: ${destination.malariaRisk}`;
        }

        if (destination.generalAdvice) {
          notes += `\n\nGeneral Advice: ${destination.generalAdvice}`;
        }

        // Add pediatric-specific notes
        if (age !== undefined && age < 18) {
          notes += `\n\n👶 PEDIATRIC PATIENT (Age: ${age} years)`;

          const vaccineLower = vaccineName.toLowerCase();

          if (vaccineLower.includes("yellow fever")) {
            if (age < 2) {
              notes += `\n⚠️ Yellow Fever vaccine should be used with caution in children under 2 years. Specialist advice recommended.`;
            }
          }

          if (vaccineLower.includes("typhoid")) {
            if (age < 6) {
              notes += `\n⚠️ Oral typhoid vaccine not suitable - use injectable form only for children under 6 years.`;
            }
          }

          if (vaccineLower.includes("hepatitis a")) {
            if (age < 2) {
              notes += `\n⚠️ Pediatric dose: 0.5ml (720 ELISA units) for children 1-15 years.`;
            }
          }

          if (vaccineLower.includes("rabies")) {
            notes += `\n⚠️ Pediatric dose: Same volume as adults but may require different schedule. Check patient's weight for dosing.`;
          }

          if (vaccineLower.includes("japanese encephalitis")) {
            if (age < 3) {
              notes += `\n⚠️ Pediatric dose: 0.25ml for children under 3 years (half adult dose).`;
            }
          }

          notes += `\n⚠️ Verify pediatric dosing schedule and formulation before administration.`;
        }

        notes += `\n\n📚 Source: TravelHealthPro (https://travelhealthpro.org.uk/)`;

        // Build medication name with brand if selected
        let medicationName = vaccineName;
        if (brandInfo) {
          medicationName = `${vaccineName} - ${brandInfo.brandName}`;
        }

        results.push({
          medication: medicationName,
          dosage: brandInfo?.schedule || vaccine?.schedule || "As per schedule",
          quantity: "1 course",
          duration: brandInfo?.schedule || vaccine?.schedule || "",
          type: "vaccine",
          prescribedItemId: vaccine?.id,
        });
      });
    }

    // Add malaria prescription
    if (selectedMalariaOption && travelDays) {
      const option = malariaOptions.find(
        (opt) => opt.prescribeItemId === selectedMalariaOption,
      );
      if (option) {
        const days = parseInt(travelDays);
        const weight = isChild ? parseFloat(childWeight) : undefined;
        const calculation = calculateMalariaDosing(
          selectedMalariaOption,
          days,
          isChild,
          weight,
        );

        let notes = calculation.notes;
        if (destination) {
          notes += `\n\nMalaria Risk in ${destination.name}: ${destination.malariaRisk}`;
        }
        if (isChild && weight) {
          notes += `\n\n⚠️ PEDIATRIC DOSE: Weight-based dosing for ${weight}kg child. Verify dose appropriateness before dispensing.`;
        }

        results.push({
          medication: option.medication,
          dosage: calculation.dosage,
          quantity: calculation.quantity,
          duration: calculation.duration,
          notes,
          type: "prescription",
          prescribedItemId: option.prescribeItemId,
        });
      }
    }

    if (results.length > 0) {
      setIsLoading(true);
      onComplete(results);
    }
  };

  const handleNext = () => {
    if (step === "destination" && selectedDestination) {
      // Check if there are outbreaks and user hasn't confirmed - they'll see inline warning
      if (outbreaks.length > 0 && !outbreakConfirmed) {
        // Don't proceed - user needs to acknowledge the inline warning first
        return;
      }
      setStep("selection");
    } else if (step === "selection") {
      if (needVaccinations) {
        setStep("vaccinations");
      } else if (needMalaria) {
        setStep("malaria-duration");
      }
    } else if (step === "vaccinations") {
      if (needMalaria) {
        setStep("malaria-duration");
      } else {
        setStep("review");
      }
    } else if (step === "malaria-duration" && travelDays) {
      setStep("malaria-medication");
    } else if (step === "malaria-medication" && selectedMalariaOption) {
      setStep("review");
    }
  };

  const handleBack = () => {
    if (step === "selection") {
      setStep("destination");
    } else if (step === "vaccinations") {
      setStep("selection");
    } else if (step === "malaria-duration") {
      if (needVaccinations) {
        setStep("vaccinations");
      } else {
        setStep("selection");
      }
    } else if (step === "malaria-medication") {
      setStep("malaria-duration");
    } else if (step === "review") {
      if (needMalaria) {
        setStep("malaria-medication");
      } else if (needVaccinations) {
        setStep("vaccinations");
      }
    }
  };

  const canProceed = () => {
    if (step === "destination") {
      // Can't proceed if checking outbreaks or if there are outbreaks that haven't been confirmed
      if (isCheckingOutbreaks) return false;
      if (outbreaks.length > 0 && !outbreakConfirmed) return false;
      return !!selectedDestination;
    }
    if (step === "selection") return needVaccinations || needMalaria;
    if (step === "vaccinations") return true; // Can skip vaccines
    if (step === "malaria-duration") {
      // For children, require both weight and travel days
      if (isChild) {
        return !!travelDays && !!childWeight && parseFloat(childWeight) > 0;
      }
      return !!travelDays;
    }
    if (step === "malaria-medication") return !!selectedMalariaOption;
    return true;
  };

  const getRiskBadgeColor = (level: string) => {
    switch (level) {
      case "high":
        return isDarkMode
          ? "bg-red-900/30 text-red-300 border-red-700"
          : "bg-red-100 text-red-700 border-red-200";
      case "medium":
        return isDarkMode
          ? "bg-amber-900/30 text-amber-300 border-amber-700"
          : "bg-amber-100 text-amber-700 border-amber-200";
      case "low":
        return isDarkMode
          ? "bg-green-900/30 text-green-300 border-green-700"
          : "bg-green-100 text-green-700 border-green-200";
      default:
        return isDarkMode
          ? "bg-gray-700 text-gray-300 border-gray-600"
          : "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const getVaccineTypeColor = (type: string) => {
    switch (type) {
      case "required":
        return isDarkMode
          ? "bg-red-900/20 border-red-700"
          : "bg-red-50 border-red-200";
      case "recommended":
        return isDarkMode
          ? "bg-blue-900/20 border-blue-700"
          : "bg-blue-50 border-blue-200";
      case "optional":
        return isDarkMode
          ? "bg-gray-800 border-gray-600"
          : "bg-gray-50 border-gray-200";
      default:
        return isDarkMode
          ? "bg-gray-800 border-gray-600"
          : "bg-gray-50 border-gray-200";
    }
  };

  const getVaccineTypeBadge = (type: string) => {
    switch (type) {
      case "required":
        return isDarkMode
          ? "border-red-700 bg-red-900/30 text-red-300"
          : "border-red-300 bg-red-100 text-red-800";
      case "recommended":
        return isDarkMode
          ? "border-blue-700 bg-blue-900/30 text-blue-300"
          : "border-blue-300 bg-blue-100 text-blue-800";
      case "optional":
        return isDarkMode
          ? "border-gray-600 bg-gray-700 text-gray-300"
          : "border-gray-300 bg-gray-100 text-gray-700";
      default:
        return isDarkMode
          ? "border-gray-600 bg-gray-700 text-gray-300"
          : "border-gray-300 bg-gray-100 text-gray-700";
    }
  };

  const getFilteredVaccines = () => {
    if (!destination) return [];

    return destination.vaccines.filter((vaccine) => {
      // Age-based filtering based on UK travel vaccine guidelines
      if (age !== undefined) {
        const vaccineName = vaccine.name.toLowerCase();

        // Yellow Fever - minimum age 9 months (use with caution 6-9 months)
        if (
          vaccine.name === "Yellow Fever" ||
          vaccineName.includes("yellow fever")
        ) {
          if (age < 1) {
            return false; // Not recommended for infants under 9 months
          }
        }

        // Japanese Encephalitis - minimum age 2 months
        if (
          vaccine.name === "Japanese Encephalitis" ||
          vaccineName.includes("japanese encephalitis")
        ) {
          if (age < 1) {
            return false; // Typically not given to infants
          }
        }

        // Meningococcal vaccines - minimum age 2 months for MenACWY
        if (
          vaccine.name === "Meningococcal Meningitis" ||
          vaccineName.includes("meningococcal")
        ) {
          if (age < 1) {
            return false; // Usually given from 1 year onwards for travel
          }
        }

        // Rabies - can be given to any age but different schedules for children
        // (no filtering needed, but schedule may differ)

        // Hepatitis A - minimum age 1 year
        if (
          vaccine.name === "Hepatitis A" ||
          vaccineName.includes("hepatitis a")
        ) {
          if (age < 1) {
            return false; // Not recommended for infants under 1 year
          }
        }

        // Typhoid (injectable) - minimum age 2 years
        // Oral typhoid - minimum age 6 years
        if (vaccine.name === "Typhoid" || vaccineName.includes("typhoid")) {
          if (age < 2) {
            return false; // Injectable form from 2 years, oral from 6 years
          }
        }

        // Hepatitis B - can be given from birth (no filtering needed)

        // Cholera/travellers' diarrhea (Dukoral) - minimum age 2 years
        if (
          vaccine.name === "Cholera" ||
          vaccineName.includes("cholera") ||
          vaccineName.includes("dukoral")
        ) {
          if (age < 2) {
            return false; // Not licensed for children under 2 years
          }
        }

        // Tick-borne Encephalitis - minimum age 1 year
        if (
          vaccine.name === "Tick-borne Encephalitis" ||
          vaccineName.includes("tick")
        ) {
          if (age < 1) {
            return false; // Not recommended for infants under 1 year
          }
        }

        // Dengue vaccine - typically only for those 9 years and older with prior dengue infection
        if (vaccine.name === "Dengue" || vaccineName.includes("dengue")) {
          if (age < 9) {
            return false; // Not recommended for children under 9 years
          }
        }

        // HPV vaccine - typically given from age 9-12 onwards
        if (
          vaccineName.includes("hpv") ||
          vaccineName.includes("papillomavirus")
        ) {
          if (age < 9) {
            return false; // Not typically recommended for children under 9 years
          }
        }
      }

      return true;
    });
  };

  const getFilteredMalariaOptions = () => {
    return malariaOptions.filter((option) => {
      // Filter by allergies
      if (patientInfo?.allergies) {
        const allergyNames = patientInfo.allergies
          .filter((a: any) => a && a.name)
          .map((a: any) => a.name.toLowerCase());
        if (
          allergyNames.some((allergy: string) =>
            option.medication.toLowerCase().includes(allergy),
          )
        ) {
          return false;
        }
      }

      // Filter by age and weight for children
      if (age !== undefined) {
        const weight = parseFloat(childWeight);

        // Utovlan 5mg tablets not suitable for children under 12
        if (
          option.prescribeItemId === "59a2ac8b-bfa2-4d57-86c8-737565944379" &&
          age < 12
        ) {
          return false;
        }

        // Atovaquone/Proguanil requires weight > 11kg
        if (
          option.prescribeItemId === "ce251889-3b48-487a-bbf3-c48fd479fe0f" &&
          isChild &&
          weight &&
          weight < 11
        ) {
          return false;
        }

        // Mefloquine requires weight > 5kg
        if (
          option.prescribeItemId === "70e08799-ff56-4f96-abeb-87f559ad2f77" &&
          isChild &&
          weight &&
          weight < 5
        ) {
          return false;
        }
      }

      return true;
    });
  };

  const getStepNumber = () => {
    const steps = ["destination", "selection"];
    if (needVaccinations) steps.push("vaccinations");
    if (needMalaria) steps.push("malaria-duration", "malaria-medication");
    steps.push("review");
    return steps.indexOf(step) + 1;
  };

  const getTotalSteps = () => {
    let total = 2; // destination + selection
    if (needVaccinations) total += 1;
    if (needMalaria) total += 2; // malaria-duration + malaria-medication
    total += 1; // review
    return total;
  };

  return (
    <div
      className={`flex flex-col h-full ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
    >
      {/* Scrollable content - flex-1 to push footer down when overflowing */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-6 p-4 pr-1">
        {/* Patient Summary Card */}
        {patientInfo && (age !== undefined || gender) && (
          <Card
            className={
              isDarkMode
                ? "bg-gradient-to-r from-indigo-900/30 to-blue-900/30 border-indigo-700"
                : "bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200"
            }
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? "bg-indigo-900/50" : "bg-indigo-100"}`}
                >
                  <Info className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h4
                    className={`font-semibold mb-1 ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                  >
                    Patient Information
                  </h4>
                  <div className="flex flex-wrap gap-3 text-sm">
                    {age !== undefined && (
                      <div className="flex items-center gap-1.5">
                        <span
                          className={
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }
                        >
                          Age:
                        </span>
                        <span
                          className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                        >
                          {age} years
                          {age < 18 && (
                            <span
                              className={`ml-1 text-xs px-1.5 py-0.5 rounded ${isDarkMode ? "bg-purple-900/30 text-purple-300" : "bg-purple-100 text-purple-700"}`}
                            >
                              Child
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    {gender && (
                      <div className="flex items-center gap-1.5">
                        <span
                          className={
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }
                        >
                          Gender:
                        </span>
                        <span
                          className={`font-medium capitalize ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                        >
                          {gender}
                        </span>
                      </div>
                    )}
                    {isChild && childWeight && (
                      <div className="flex items-center gap-1.5">
                        <span
                          className={
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }
                        >
                          Weight:
                        </span>
                        <span
                          className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                        >
                          {childWeight} kg
                        </span>
                      </div>
                    )}
                  </div>
                  <p
                    className={`text-xs mt-2 ${isDarkMode ? "text-indigo-300" : "text-indigo-700"}`}
                  >
                    Recommendations are tailored to this patient's age
                    {isChild && childWeight && " and weight"}.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {/* Progress Indicator */}
        {step !== "destination" && (
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div
                className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Step {getStepNumber()} of {getTotalSteps()}
              </div>
            </div>
            <div
              className={`flex-1 mx-4 h-2 rounded-full overflow-hidden ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
            >
              <div
                className="bg-purple-600 h-full transition-all duration-300"
                style={{
                  width: `${(getStepNumber() / getTotalSteps()) * 100}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Step 1: Destination Selection */}
        {step === "destination" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="destination">Destination *</Label>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between"
                  >
                    {selectedDestination
                      ? travelDestinations?.find(
                          (dest) =>
                            (dest.id || (dest as any)._id) ===
                            selectedDestination,
                        )?.name
                      : "Choose your destination..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[500px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search countries or regions..." />
                    <CommandList className="max-h-[400px]">
                      <CommandEmpty>No destination found.</CommandEmpty>
                      <CommandGroup>
                        {travelDestinations?.map((dest) => (
                          <CommandItem
                            key={dest.id || (dest as any)._id}
                            value={`${dest.name} ${dest.region}`}
                            onSelect={() => {
                              setSelectedDestination(
                                dest.id || (dest as any)._id,
                              );
                              setRiskLevel(""); // Reset to allow auto-detection
                              setOutbreakConfirmed(false); // Reset outbreak confirmation
                              setOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedDestination ===
                                  (dest.id || (dest as any)._id)
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            <div className="flex items-center justify-between flex-1 gap-2">
                              <span className="flex-1">{dest.name}</span>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {dest.yellowFeverRequired && (
                                  <span
                                    className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded"
                                    title="Yellow Fever Required"
                                  >
                                    YF
                                  </span>
                                )}
                                {dest.malariaRisk && (
                                  <span
                                    className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded"
                                    title="Malaria Risk"
                                  >
                                    M
                                  </span>
                                )}
                                <span className="text-xs text-gray-500">
                                  ({dest.region})
                                </span>
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <div
                className={`flex items-start gap-2 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p>
                    Search for your destination. Badges: <strong>YF</strong> =
                    Yellow Fever required, <strong>M</strong> = Malaria risk
                  </p>
                </div>
              </div>
            </div>

            {isCheckingOutbreaks && (
              <Card
                className={
                  isDarkMode
                    ? "bg-blue-900/20 border-blue-700"
                    : "bg-blue-50 border-blue-200"
                }
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <p
                      className={`text-sm ${isDarkMode ? "text-blue-300" : "text-blue-800"}`}
                    >
                      Checking for active outbreaks...
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {destination && (
              <Card
                className={
                  isDarkMode
                    ? "bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-700"
                    : "bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200"
                }
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? "bg-blue-900/50" : "bg-blue-100"}`}
                    >
                      <MapPin className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h4
                          className={`font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                        >
                          {destination.name}
                        </h4>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full border font-medium ${getRiskBadgeColor(destination.riskLevel)}`}
                        >
                          {destination.riskLevel.toUpperCase()} RISK
                        </span>
                        {destination.yellowFeverRequired && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full border font-medium ${isDarkMode ? "border-red-700 bg-red-900/30 text-red-300" : "border-red-300 bg-red-100 text-red-800"}`}
                          >
                            YELLOW FEVER REQUIRED
                          </span>
                        )}
                      </div>
                      <p
                        className={`text-sm mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        {destination.region}
                      </p>

                      {destination.malariaRisk && (
                        <Card
                          className={`mb-2 ${isDarkMode ? "bg-orange-900/20 border-orange-700" : "bg-orange-50 border-orange-200"}`}
                        >
                          <CardContent className="p-2">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p
                                  className={`text-xs font-medium ${isDarkMode ? "text-orange-300" : "text-orange-900"}`}
                                >
                                  Malaria Risk
                                </p>
                                <p
                                  className={`text-xs ${isDarkMode ? "text-orange-400" : "text-orange-800"}`}
                                >
                                  {destination.malariaRisk}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {destination.generalAdvice && (
                        <Card
                          className={
                            isDarkMode
                              ? "bg-gray-800/50 border-blue-700"
                              : "bg-white/50 border-blue-200"
                          }
                        >
                          <CardContent className="p-3">
                            <div className="flex items-start gap-2">
                              <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                              <p
                                className={`text-xs ${isDarkMode ? "text-blue-300" : "text-blue-900"}`}
                              >
                                {destination.generalAdvice}
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Inline Outbreak Warning */}
                      {outbreaks.length > 0 && (
                        <Card
                          className={`mt-2 ${isDarkMode ? "bg-red-900/20 border-red-700" : "bg-red-50 border-red-300"}`}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-start gap-2 mb-2">
                              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p
                                  className={`text-sm font-semibold mb-1 ${isDarkMode ? "text-red-300" : "text-red-900"}`}
                                >
                                  Active Outbreak Warning
                                </p>
                                <p
                                  className={`text-xs mb-2 ${isDarkMode ? "text-red-400" : "text-red-800"}`}
                                >
                                  There {outbreaks.length === 1 ? "is" : "are"}{" "}
                                  currently <strong>{outbreaks.length}</strong>{" "}
                                  active outbreak
                                  {outbreaks.length !== 1 ? "s" : ""} reported
                                  in {destination.name}
                                </p>
                              </div>
                            </div>

                            <div className="space-y-2">
                              {outbreaks.map((outbreak: any, index: number) => (
                                <div
                                  key={index}
                                  className={`rounded p-2 border ${isDarkMode ? "bg-gray-800/70 border-red-800" : "bg-white/70 border-red-200"}`}
                                >
                                  <p
                                    className={`text-xs font-medium mb-1 ${isDarkMode ? "text-red-300" : "text-red-900"}`}
                                  >
                                    {outbreak.title}
                                  </p>
                                  <div
                                    className={`text-xs mb-2 line-clamp-2 ${isDarkMode ? "text-red-400" : "text-red-800"}`}
                                    dangerouslySetInnerHTML={{
                                      __html: outbreak.description,
                                    }}
                                  />
                                  <div className="flex items-center justify-between text-xs">
                                    <span
                                      className={
                                        isDarkMode
                                          ? "text-red-400"
                                          : "text-red-700"
                                      }
                                    >
                                      {getOutbreakDateLabel(outbreak)}
                                    </span>
                                    {outbreak.link && (
                                      <a
                                        href={outbreak.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`text-sm font-normal underline ${isDarkMode ? "text-red-400 hover:text-red-300" : "text-red-700 hover:text-red-900"}`}
                                      >
                                        Read more
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {!outbreakConfirmed && (
                              <div
                                className={`mt-3 pt-3 border-t ${isDarkMode ? "border-red-800" : "border-red-200"}`}
                              >
                                <div className="flex items-start gap-2 mb-2">
                                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                  <p
                                    className={`text-xs ${isDarkMode ? "text-amber-300" : "text-amber-900"}`}
                                  >
                                    Please review this outbreak information
                                    before continuing. Additional precautions
                                    may be required.
                                  </p>
                                </div>
                                <Button
                                  onClick={() => setOutbreakConfirmed(true)}
                                  className="w-full bg-red-600 hover:bg-red-700 text-white text-sm h-8"
                                >
                                  I Understand, Continue
                                </Button>
                              </div>
                            )}

                            {outbreakConfirmed && (
                              <div
                                className={`mt-3 pt-3 border-t ${isDarkMode ? "border-red-800" : "border-red-200"}`}
                              >
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                  <p
                                    className={`text-xs font-medium ${isDarkMode ? "text-green-400" : "text-green-800"}`}
                                  >
                                    Outbreak warning acknowledged
                                  </p>
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 2: Selection - What do you need? */}
        {step === "selection" && destination && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">
                What do you need for your trip?
              </h3>
              <p
                className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Select the travel health services you need for{" "}
                {destination.name}
              </p>
            </div>

            <div className="space-y-3">
              <Card
                className={`cursor-pointer transition-all hover:shadow-md ${
                  needVaccinations
                    ? "border-purple-600 " +
                      (isDarkMode ? "bg-purple-900/20" : "bg-purple-50")
                    : isDarkMode
                      ? "hover:bg-gray-800"
                      : "hover:bg-gray-50"
                }`}
                onClick={() => setNeedVaccinations(!needVaccinations)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="need-vaccines"
                      checked={needVaccinations}
                      onCheckedChange={(checked) =>
                        setNeedVaccinations(checked as boolean)
                      }
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Syringe className="w-5 h-5 text-purple-600" />
                        <Label
                          htmlFor="need-vaccines"
                          className={`font-semibold cursor-pointer ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                        >
                          Vaccinations
                        </Label>
                      </div>
                      <p
                        className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Get recommended and required vaccines for{" "}
                        {destination.name}
                      </p>
                      <p className="text-xs text-purple-600 mt-1">
                        {destination.vaccines.length} vaccine
                        {destination.vaccines.length !== 1 ? "s" : ""} available
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {hasMalariaRisk && (
                <Card
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    needMalaria
                      ? "border-orange-600 " +
                        (isDarkMode ? "bg-orange-900/20" : "bg-orange-50")
                      : isDarkMode
                        ? "hover:bg-gray-800"
                        : "hover:bg-gray-50"
                  }`}
                  onClick={() => setNeedMalaria(!needMalaria)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="need-malaria"
                        checked={needMalaria}
                        onCheckedChange={(checked) =>
                          setNeedMalaria(checked as boolean)
                        }
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Pill className="w-5 h-5 text-orange-600" />
                          <Label
                            htmlFor="need-malaria"
                            className={`font-semibold cursor-pointer ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                          >
                            Malaria Prevention
                          </Label>
                        </div>
                        <p
                          className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          Get antimalarial medication prescription
                        </p>
                        <div className="flex items-center gap-1 mt-1">
                          <AlertCircle className="w-3 h-3 text-orange-600" />
                          <p className="text-xs text-orange-600 font-medium">
                            {destination.malariaRisk}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {!hasMalariaRisk && (
                <Card
                  className={
                    isDarkMode
                      ? "bg-green-900/20 border-green-700"
                      : "bg-green-50 border-green-200"
                  }
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p
                          className={`text-sm font-medium ${isDarkMode ? "text-green-300" : "text-green-900"}`}
                        >
                          No malaria risk
                        </p>
                        <p
                          className={`text-xs ${isDarkMode ? "text-green-400" : "text-green-700"}`}
                        >
                          {destination.name} does not have malaria risk.
                          Antimalarial medication is not needed.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Vaccinations */}
        {step === "vaccinations" && destination && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">
                Select Vaccinations
              </h3>
              <p
                className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Choose the vaccines you need for {destination.name}
              </p>
            </div>

            {isChild && (
              <Card
                className={
                  isDarkMode
                    ? "bg-purple-900/20 border-purple-700"
                    : "bg-purple-50 border-purple-200"
                }
              >
                <CardContent className="p-3">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div
                      className={`text-xs ${isDarkMode ? "text-purple-300" : "text-purple-800"}`}
                    >
                      <p className="font-medium mb-1">
                        Age-Appropriate Vaccines ({age} years old)
                      </p>
                      <p>
                        Only vaccines suitable for this patient's age are shown.
                        Some vaccines have minimum age requirements:
                        {age &&
                          age < 1 &&
                          " Infants under 1 year have limited vaccine options."}
                        {age &&
                          age >= 1 &&
                          age < 2 &&
                          " Hepatitis A and Yellow Fever available from 1 year."}
                        {age &&
                          age >= 2 &&
                          age < 6 &&
                          " Typhoid (injectable) available from 2 years."}
                        {age &&
                          age >= 6 &&
                          age < 9 &&
                          " Oral typhoid available from 6 years."}
                        {age &&
                          age >= 9 &&
                          " Most travel vaccines available with age-appropriate dosing."}
                      </p>
                      <p className="mt-1 font-medium">
                        ⚠️ Pediatric doses and schedules may differ from adult
                        formulations.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* NaTHNaC Disclaimer */}
            <Card
              className={
                isDarkMode
                  ? "bg-amber-900/20 border-amber-700"
                  : "bg-amber-50 border-amber-200"
              }
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div
                    className={`text-sm ${isDarkMode ? "text-amber-300" : "text-amber-800"}`}
                  >
                    <p className="font-medium mb-1">
                      Important: Verify vaccine recommendations
                    </p>
                    <p>
                      Please cross-check the vaccines listed below against the{" "}
                      <a
                        href={getTravelHealthProUrl(destination.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-1 font-medium underline ${isDarkMode ? "text-amber-200 hover:text-amber-100" : "text-amber-900 hover:text-amber-700"}`}
                      >
                        NaTHNaC Travel Health Pro website
                        <ExternalLink className="w-3 h-3" />
                      </a>{" "}
                      to ensure the correct vaccines have been selected for{" "}
                      {destination.name}. Recommendations may change based on
                      current outbreaks and travel advisories.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-2">
              {getFilteredVaccines().map((vaccine) => (
                <Card
                  key={vaccine.name}
                  className={`${getVaccineTypeColor(vaccine.type)} transition-all hover:shadow-md`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id={vaccine.name}
                        checked={selectedVaccines.includes(vaccine.name)}
                        onCheckedChange={(checked) =>
                          handleVaccineToggle(vaccine.name, checked as boolean)
                        }
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Label
                            htmlFor={vaccine.name}
                            className={`font-semibold cursor-pointer ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                          >
                            {vaccine.name}
                          </Label>
                          {isChild && (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full border font-medium ${isDarkMode ? "border-green-700 bg-green-900/30 text-green-300" : "border-green-300 bg-green-100 text-green-800"}`}
                            >
                              ✓ AGE-APPROPRIATE
                            </span>
                          )}
                        </div>
                        <p
                          className={`text-sm mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          {vaccine.description}
                        </p>
                        {vaccine.schedule && (
                          <div
                            className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            <span className="font-medium">Schedule:</span>{" "}
                            {vaccine.schedule}
                          </div>
                        )}
                        {vaccine.notes && (
                          <div
                            className={`text-xs rounded p-2 mt-2 ${isDarkMode ? "text-gray-400 bg-gray-800/50" : "text-gray-600 bg-white/50"}`}
                          >
                            <span className="font-medium">Note:</span>{" "}
                            {vaccine.notes}
                          </div>
                        )}

                        {/* Brand Selection - Show when vaccine is selected */}
                        {selectedVaccines.includes(vaccine.name) &&
                          (() => {
                            const brands = getBrandsForVaccine(vaccine.name);
                            if (brands.length > 0) {
                              return (
                                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                  <Label
                                    htmlFor={`brand-${vaccine.name}`}
                                    className={`text-sm font-medium mb-2 block ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                                  >
                                    Select Brand
                                  </Label>
                                  <Select
                                    value={vaccineBrands[vaccine.name] || ""}
                                    onValueChange={(value) =>
                                      handleBrandChange(vaccine.name, value)
                                    }
                                  >
                                    <SelectTrigger
                                      id={`brand-${vaccine.name}`}
                                      className="w-full"
                                    >
                                      <SelectValue placeholder="Choose brand..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {brands.map((brand) => (
                                        <SelectItem
                                          key={brand.id}
                                          value={brand.id}
                                        >
                                          <div className="flex flex-col">
                                            <span className="font-medium">
                                              {brand.brandName}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                              {brand.manufacturer}
                                            </span>
                                          </div>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              );
                            }
                            return null;
                          })()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {selectedVaccines.length > 0 && (
              <Card
                className={
                  isDarkMode
                    ? "bg-green-900/20 border-green-700"
                    : "bg-green-50 border-green-200"
                }
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div className="flex-1">
                      <p
                        className={`text-sm font-medium ${isDarkMode ? "text-green-300" : "text-green-900"}`}
                      >
                        {selectedVaccines.length} vaccine
                        {selectedVaccines.length !== 1 ? "s" : ""} selected
                      </p>
                      <p
                        className={`text-xs ${isDarkMode ? "text-green-400" : "text-green-700"}`}
                      >
                        {selectedVaccines.join(", ")}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {selectedVaccines.length === 0 && (
              <Card
                className={
                  isDarkMode
                    ? "bg-blue-900/20 border-blue-700"
                    : "bg-blue-50 border-blue-200"
                }
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div
                      className={`text-sm ${isDarkMode ? "text-blue-300" : "text-blue-800"}`}
                    >
                      <p className="font-medium mb-1">No vaccines selected</p>
                      <p>
                        You can skip vaccination selection and continue
                        {needMalaria ? " to malaria prevention" : ""}, or select
                        vaccines above.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {isChild &&
              destination &&
              (() => {
                const filteredCount = getFilteredVaccines().length;
                const totalCount = destination.vaccines.length;
                const excludedCount = totalCount - filteredCount;

                if (excludedCount > 0) {
                  return (
                    <Card
                      className={
                        isDarkMode
                          ? "bg-amber-900/20 border-amber-700"
                          : "bg-amber-50 border-amber-200"
                      }
                    >
                      <CardContent className="p-3">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div
                            className={`text-xs ${isDarkMode ? "text-amber-300" : "text-amber-800"}`}
                          >
                            <p className="font-medium mb-1">
                              {excludedCount} vaccine
                              {excludedCount !== 1 ? "s" : ""} excluded due to
                              age
                            </p>
                            <p>
                              Some vaccines are not recommended for children
                              under certain ages. The following vaccines are not
                              shown because they're not suitable for a {age}
                              -year-old:
                            </p>
                            <ul className="list-disc list-inside mt-1 space-y-0.5">
                              {destination.vaccines
                                .filter(
                                  (v) => !getFilteredVaccines().includes(v),
                                )
                                .map((v) => (
                                  <li key={v.name}>{v.name}</li>
                                ))}
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                }
                return null;
              })()}
          </div>
        )}

        {/* Step 4: Malaria - Travel Duration */}
        {step === "malaria-duration" && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">
                Travel Duration{isChild && " & Weight"}
              </h3>
              <p
                className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {isChild
                  ? "Provide travel duration and patient weight for accurate dosing"
                  : "How long will you be in the malaria risk area?"}
              </p>
            </div>

            {isChild && (
              <div className="space-y-2 mb-4">
                <Label htmlFor="child-weight" className="text-base font-medium">
                  Patient Weight (kg) *
                </Label>
                <Input
                  id="child-weight"
                  type="number"
                  min="1"
                  step="0.1"
                  placeholder="e.g., 25.5"
                  value={childWeight}
                  onChange={(e) => setChildWeight(e.target.value)}
                  className="text-lg"
                />
                <p
                  className={`text-xs flex items-start gap-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                >
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>
                    Weight is required for children to calculate appropriate
                    malaria medication dosing. Some medications have minimum
                    weight requirements.
                  </span>
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="travel-days" className="text-base font-medium">
                Number of days in malaria risk area *
              </Label>
              <Input
                id="travel-days"
                type="number"
                min="1"
                max="21"
                step="1"
                placeholder="e.g., 14"
                value={travelDays}
                onKeyDown={(e) => {
                  if ([".", "e", "E", "+", "-"].includes(e.key)) {
                    e.preventDefault(); // 🚫 block unwanted characters
                  }
                }}
                onChange={(e) => setTravelDays(e.target.value)}
                className="text-lg"
              />
            </div>

            <Card
              className={
                isDarkMode
                  ? "bg-blue-900/20 border-blue-700"
                  : "bg-blue-50 border-blue-200"
              }
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div
                    className={`text-sm ${isDarkMode ? "text-blue-300" : "text-blue-800"}`}
                  >
                    <p className="font-medium mb-1">Important</p>
                    <p>
                      Antimalarial medications need to be started before travel
                      and continued after return. We'll calculate the total
                      quantity needed based on the duration.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 5: Malaria - Medication Selection */}
        {step === "malaria-medication" && travelDays && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">
                Select Antimalarial Medication
              </h3>
              <p
                className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Choose the most appropriate medication for {travelDays} days in
                a {riskLevel} risk area
              </p>
            </div>

            {isChild && childWeight && (
              <Card
                className={
                  isDarkMode
                    ? "bg-purple-900/20 border-purple-700"
                    : "bg-purple-50 border-purple-200"
                }
              >
                <CardContent className="p-3">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div
                      className={`text-xs ${isDarkMode ? "text-purple-300" : "text-purple-800"}`}
                    >
                      <p className="font-medium mb-1">Pediatric Dosing</p>
                      <p>
                        Dosages have been calculated based on the child's weight
                        ({childWeight}kg). Some medications may not be suitable
                        or require different formulations. Options have been
                        filtered accordingly.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <RadioGroup
              value={selectedMalariaOption}
              onValueChange={setSelectedMalariaOption}
            >
              <div className="space-y-3">
                {getFilteredMalariaOptions().map((option) => {
                  const days = parseInt(travelDays);
                  const weight = isChild ? parseFloat(childWeight) : undefined;
                  const calculation = calculateMalariaDosing(
                    option.prescribeItemId,
                    days,
                    isChild,
                    weight,
                  );
                  return (
                    <Card
                      key={option.prescribeItemId}
                      className={`cursor-pointer transition-colors ${
                        selectedMalariaOption === option.prescribeItemId
                          ? "border-blue-600 " +
                            (isDarkMode ? "bg-blue-900/20" : "bg-blue-50")
                          : isDarkMode
                            ? "hover:bg-gray-800"
                            : "hover:bg-gray-50"
                      }`}
                      onClick={() =>
                        setSelectedMalariaOption(option.prescribeItemId)
                      }
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <RadioGroupItem
                            value={option.prescribeItemId}
                            id={option.prescribeItemId}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <Label
                              htmlFor={option.prescribeItemId}
                              className="font-semibold cursor-pointer text-base"
                            >
                              {[option?.name, option?.medicationType]
                                .filter(Boolean)
                                .join(" ")}
                              {isChild && weight && (
                                <span
                                  className={`ml-2 text-xs px-2 py-0.5 rounded font-normal ${isDarkMode ? "bg-purple-900/30 text-purple-300" : "bg-purple-100 text-purple-700"}`}
                                >
                                  Child dose ({weight}kg)
                                </span>
                              )}
                            </Label>
                            <div
                              className={`mt-2 space-y-1 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                            >
                              <div>
                                <span className="font-medium">Dosage:</span>{" "}
                                {calculation.dosage}
                              </div>
                              <div>
                                <span className="font-medium">Quantity:</span>{" "}
                                {calculation.quantity}
                              </div>
                              <div>
                                <span className="font-medium">Duration:</span>{" "}
                                {calculation.duration}
                              </div>
                              <div
                                className={`text-xs mt-2 rounded p-2 ${isDarkMode ? "text-gray-400 bg-gray-800" : "text-gray-600 bg-gray-50"}`}
                              >
                                {calculation.notes}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Step 7: Summary */}
        {step === "review" && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">
                Travel Health Summary
              </h3>
              <p
                className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Review your selections and create draft vaccinations and/or
                prescription
              </p>
            </div>

            {destination && (
              <Card
                className={
                  isDarkMode
                    ? "bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-700"
                    : "bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200"
                }
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    <h4
                      className={`font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                    >
                      Destination
                    </h4>
                  </div>
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    {destination.name}, {destination.region}
                  </p>
                </CardContent>
              </Card>
            )}

            {needVaccinations && selectedVaccines.length > 0 && (
              <Card
                className={
                  isDarkMode
                    ? "bg-purple-900/20 border-purple-700"
                    : "bg-purple-50 border-purple-200"
                }
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Syringe className="w-5 h-5 text-purple-600" />
                    <h4
                      className={`font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                    >
                      Vaccinations
                    </h4>
                  </div>
                  <div className="space-y-2">
                    {selectedVaccines.map((name, idx) => {
                      const brandId = vaccineBrands[name];
                      const brandInfo = brandId
                        ? getVaccineById(brandId)
                        : null;

                      return (
                        <div
                          key={idx}
                          className="flex items-start gap-2 text-sm"
                        >
                          <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div
                              className={
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              }
                            >
                              {name}
                            </div>
                            {brandInfo && (
                              <div
                                className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                {brandInfo.brandName} ({brandInfo.manufacturer})
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {needMalaria && selectedMalariaOption && travelDays && (
              <Card
                className={
                  isDarkMode
                    ? "bg-orange-900/20 border-orange-700"
                    : "bg-orange-50 border-orange-200"
                }
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Pill className="w-5 h-5 text-orange-600" />
                    <h4
                      className={`font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                    >
                      Malaria Prevention
                    </h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <Package className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p
                          className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                        >
                          {
                            malariaOptions.find(
                              (o) =>
                                o.prescribeItemId === selectedMalariaOption,
                            )?.name
                          }
                          {isChild && childWeight && (
                            <span
                              className={`ml-2 text-xs px-2 py-0.5 rounded font-normal ${isDarkMode ? "bg-purple-900/30 text-purple-300" : "bg-purple-100 text-purple-700"}`}
                            >
                              Child dose ({childWeight}kg)
                            </span>
                          )}
                        </p>
                        <p
                          className={
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }
                        >
                          {
                            calculateMalariaDosing(
                              selectedMalariaOption,
                              parseInt(travelDays),
                              isChild,
                              isChild ? parseFloat(childWeight) : undefined,
                            ).quantity
                          }
                        </p>
                        <p
                          className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          {
                            calculateMalariaDosing(
                              selectedMalariaOption,
                              parseInt(travelDays),
                              isChild,
                              isChild ? parseFloat(childWeight) : undefined,
                            ).duration
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card
              className={
                isDarkMode
                  ? "bg-green-900/20 border-green-700"
                  : "bg-green-50 border-green-200"
              }
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p
                      className={`text-sm font-medium mb-1 ${isDarkMode ? "text-green-300" : "text-green-900"}`}
                    >
                      Ready to Complete
                    </p>
                    <p
                      className={`text-xs mb-3 ${isDarkMode ? "text-green-400" : "text-green-800"}`}
                    >
                      Click the Complete button below to create all selected
                      items as drafts in the patient's consultation.
                    </p>
                    <div
                      className={`text-xs ${isDarkMode ? "text-green-400" : "text-green-700"}`}
                    >
                      {selectedVaccines.length > 0 && (
                        <div className="flex items-center gap-1">
                          <Syringe className="w-3 h-3" />
                          <span>
                            {selectedVaccines.length} vaccine draft
                            {selectedVaccines.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                      )}
                      {selectedMalariaOption && (
                        <div className="flex items-center gap-1">
                          <Pill className="w-3 h-3" />
                          <span>1 malaria prescription</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      {/* end scrollable content */}

      {/* Flexible footer - flows naturally with content, sticks when content overflows */}
      <div
        className={`flex items-center gap-3 pt-4 border-t flex-shrink-0 ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <Button
          variant="outline"
          onClick={() => {
            if (step === "destination") {
              // Only cancel completely if already at the beginning
              onCancel();
            } else {
              // Reset to the beginning
              setStep("destination");
              setSelectedDestination("");
              setNeedVaccinations(false);
              setNeedMalaria(false);
              setSelectedVaccines([]);
              setTravelDays("");
              setRiskLevel("");
              setSelectedMalariaOption("");
              setChildWeight(patientInfo?.weight?.toString() || "");
              setOutbreaks([]);
              setOutbreakConfirmed(false);
            }
          }}
          className="flex-1"
          disabled={isLoading}
        >
          {step === "destination" ? "Cancel" : "Start Over"}
        </Button>
        {step !== "destination" && step !== "review" && (
          <Button
            variant="outline"
            onClick={handleBack}
            className="flex-1"
            disabled={isLoading}
          >
            Back
          </Button>
        )}
        {step !== "review" ? (
          <Button
            onClick={handleNext}
            disabled={!canProceed() || isLoading}
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <>
            <Button
              variant="outline"
              onClick={handleBack}
              className="flex-1"
              disabled={isLoading}
            >
              Back
            </Button>
            <Button
              onClick={handleCreateBoth}
              disabled={
                isLoading ||
                !(selectedVaccines.length > 0 || selectedMalariaOption)
              }
              className="flex-1 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <CircularProgress
                    size={20}
                    color="inherit"
                    className="mr-2"
                  />
                  Generating...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Complete
                </>
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
